import React, { useEffect, useCallback, useState } from "react";
import { connect } from "react-redux";
import { Row } from "react-bootstrap";
import ReactDOMServer from "react-dom/server";
import { useNavigate } from "react-router-dom";
import RevolutCheckout from "@revolut/checkout";
import { createColumnHelper } from "@tanstack/react-table";

import { ViewIcon, EditCardIcon } from "../../assets";
import { ERROR_TOAST } from "../../constants/toast.constants";
import {
  SCREEN_PATH,
  EMPTY_STRING,
  subscriptionConstants,
  SUBSCRIPTION_PLAN_MAPPING,
  SUBSCRIPTION_STATUS_MAPPING,
  SUBSCRIPTION_PAYMENT_STATUS,
  SUBSCRIPTION_PLAN_TYPE_MAPPING,
} from "../../constants";
import {
  encryptObject,
  setItemToStorage,
  getDecryptPayload,
  SUBSCRIPTION_STATUS,
  USER_ROLE,
  SUBSCRIPTION_PLATFORM,
} from "../../system";
import {
  customToast,
  hasAgentAccess,
  getTimezoneOffSet,
  getFormattedValue,
  formatUnixTimestampToDate,
} from "../../shared/utility";
import ReactTable from "../../shared/components/tables/ReactTable";
import MainSpinner from "../../shared/components/spinner/MainSpinner";
import InvoiceViewer from "../../shared/components/invoice-viewer/InvoiceViewer";
import MainContentWrapper from "../../shared/components/main-content-wrapper/MainContentWrapper";
import { subscriptionActions, authActions } from "../../redux/actions";
import Topbar from "../../layout/Topbar";
import Footer from "../../layout/Footer";
import Panel from "./Panel";
import ConfirmationPopup from "../../shared/components/modals/ConfirmationPopup";

const { GET_SUBSCRIPTIONS, CHANGE_CARD, CANCEL_SUBSCRIPTION, FETCH_INVOICE } =
  subscriptionConstants;

const DetailsRow = ({ label, value, className = EMPTY_STRING }) => (
  <div className="row py-1">
    <div className="col-6 col-xl-7">
      <div>
        <span className="item-label">{label}</span>
      </div>
    </div>
    <div className="col-6 col-xl-5">
      <span className={`item-value ${className} text-ellipsis`} title={value}>
        {value}
      </span>
    </div>
  </div>
);

const MySubscriptions = (props) => {
  const {
    user,
    userRole,
    token,
    billingDetails,
    subscriptionPlan,
    subscriptionDetails,
    spinnerActions,
    getSubscriptions,
    cancelSubscription,
    resetToken,
    changeCard,
    updateUser,
    fetchInvoice,
  } = props;

  const isWebSubscription =
    user.subscriptionPlatform === SUBSCRIPTION_PLATFORM.WEB ||
    user.subscriptionPlatform === SUBSCRIPTION_PLATFORM.NONE;

  const canAllowWebSubscription =
    (user.subscriptionPlatform === SUBSCRIPTION_PLATFORM.ANDROID ||
      user.subscriptionPlatform === SUBSCRIPTION_PLATFORM.IOS) &&
    user.subscriptionStatus === SUBSCRIPTION_STATUS.EXPIRED;

  const navigate = useNavigate();

  const [confirmationModal, setConfirmationModal] = useState(false);

  const openConfirmationModal = () => setConfirmationModal(true);
  const closeConfirmationModal = () => setConfirmationModal(false);

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor("subscriptionPlan.plan", {
      header: <div className="column-head">Subscription</div>,
      cell: ({ row: { original } }) => (
        <div>
          {original.isFreeTrialPeriod
            ? SUBSCRIPTION_PLAN_MAPPING[0]
            : SUBSCRIPTION_PLAN_MAPPING[original.subscriptionPlan?.plan]}
        </div>
      ),
      size: 130,
      enableSorting: false,
    }),
    columnHelper.accessor("subscriptionPlan.planType", {
      header: <div className="column-head">Cycle</div>,
      cell: ({ row: { original } }) => (
        <div>
          {original.isFreeTrialPeriod
            ? `${original.subscriptionPlan?.trialDays} days`
            : SUBSCRIPTION_PLAN_TYPE_MAPPING[
                original.subscriptionPlan?.planType
              ]}
        </div>
      ),
      size: 100,
      enableSorting: false,
    }),
    columnHelper.accessor("paymentStatus", {
      header: <div className="column-head">Status</div>,
      cell: ({ row: { original } }) => (
        <div>{SUBSCRIPTION_STATUS_MAPPING[original.status]}</div>
      ),
      size: 100,
      enableSorting: false,
    }),
    columnHelper.accessor("status", {
      header: <div className="column-head">Payment Status</div>,
      cell: ({ row: { original } }) => (
        <div>
          {original.isFreeTrialPeriod
            ? "-"
            : original.paymentStatus === SUBSCRIPTION_PAYMENT_STATUS.PENDING
            ? "Pending"
            : "Paid"}
        </div>
      ),
      enableSorting: false,
    }),
    columnHelper.accessor("startDate", {
      header: <div className="column-head">Start Date</div>,
      cell: ({ row: { original } }) => (
        <div>{formatUnixTimestampToDate(original.startDate)}</div>
      ),
      size: 130,
      enableSorting: false,
    }),
    columnHelper.accessor("endDate", {
      header: <div className="column-head">Expiry Date</div>,
      cell: ({ row: { original } }) => (
        <div>{formatUnixTimestampToDate(original.endDate)}</div>
      ),
      size: 130,
      enableSorting: false,
    }),
    columnHelper.accessor("payment", {
      header: <div className="column-head">Total</div>,
      cell: ({ row: { original } }) => (
        <div>{`${original.priceCurrencyCode} ${original.payment}`}</div>
      ),
      size: 100,
      enableSorting: false,
    }),
    columnHelper.accessor("action", {
      header: <div className="column-head text-end">Invoice</div>,
      cell: ({ row: { original } }) =>
        original.paymentStatus !== SUBSCRIPTION_PAYMENT_STATUS.PENDING && (
          <div className="text-end pe-3">
            <img
              alt="View"
              src={ViewIcon}
              className="pointer"
              title="View"
              onClick={() => handleInvoiceClick(original.id)}
            />
          </div>
        ),
      size: 100,
      enableSorting: false,
    }),
  ];

  const spinner = useCallback(
    (type) => spinnerActions.findIndex((s) => s === type),
    [spinnerActions]
  );

  useEffect(() => {
    getSubscriptions(userRole);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (token !== EMPTY_STRING) {
      RevolutCheckout(token, process.env.REACT_APP_REVOLUT_MODE).then(
        (instance) => {
          // work with instance
          instance.payWithPopup({
            savePaymentMethodFor: process.env.REACT_APP_PAYMENT_METHOD_FOR,
            onSuccess() {
              getSubscriptions(userRole);
            },
          });
        }
      );
      resetToken();
    }
    // eslint-disable-next-line
  }, [token]);

  const navigateToSubscription = () => {
    navigate(
      userRole === USER_ROLE.USER
        ? SCREEN_PATH.SUBSCRIPTION_PLANS
        : SCREEN_PATH.AGENT_SUBSCRIPTION_PLANS,
      { replace: true }
    );
  };

  const UpdateSubscriptionStatus = () => {
    closeConfirmationModal();
    const payload = getDecryptPayload();
    const data = {
      ...payload.user,
      subscriptionStatus: SUBSCRIPTION_STATUS.CANCELLED,
    };
    const ciphertext = encryptObject({
      ...payload,
      user: { ...data },
    });
    setItemToStorage(process.env.REACT_APP_PAYLOAD, ciphertext);
    updateUser({ ...data });
  };

  const handleInvoiceClick = (subscriptionId) => {
    const body = { timeDifference: getTimezoneOffSet() };
    fetchInvoice(subscriptionId, body)
      .then((response) => {
        console.log("data", response?.data);
        if (response?.data) {
          const invoiceWindow = window.open("", "_blank");
          if (!invoiceWindow) {
            customToast(
              "Popup blocked. Please allow popups for this site.",
              ERROR_TOAST
            );
            return;
          }

          const invoiceHtml = ReactDOMServer.renderToStaticMarkup(
            <InvoiceViewer base64Data={response.data} />
          );

          invoiceWindow.document.open();
          invoiceWindow.document.write(invoiceHtml);
          invoiceWindow.document.close();
        } else {
          customToast("Invoice not available.", ERROR_TOAST);
        }
      })
      .catch((error) => {
        customToast("Failed to load the invoice.", ERROR_TOAST);
      });
  };

  const showNoOfListings = (subscriptionPlan) => {
    if (subscriptionPlan && hasAgentAccess(userRole)) {
      return ` - ${
        subscriptionPlan.freeListing + subscriptionPlan.additionalListing
      } listings`;
    }
    return EMPTY_STRING;
  };

  return (
    <>
      {(spinner(CANCEL_SUBSCRIPTION) > -1 ||
        spinner(CHANGE_CARD) > -1 ||
        spinner(FETCH_INVOICE) > -1) && (
        <MainSpinner className="backdrop-transparent" />
      )}
      <Topbar />
      <MainContentWrapper>
        <ConfirmationPopup
          message="Are you sure you want to cancel the subscription?"
          onCancel={closeConfirmationModal}
          show={confirmationModal}
          onConfirm={() =>
            cancelSubscription(userRole, UpdateSubscriptionStatus)
          }
        />
        <div className="container">
          <div
            className="
            flex-column
            flex-md-row
            align-items-start 
            align-items-md-center 
            subscriptions-header 
            "
          >
            <h2 className="mb-3 mb-md-0">My Subscriptions</h2>
            {isWebSubscription &&
              user.subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE && (
                <button
                  className="
              mx-auto 
              mx-md-0
              cancel-subscription-btn 
            "
                  onClick={openConfirmationModal}
                >
                  Cancel Subscription
                </button>
              )}
            {(canAllowWebSubscription ||
              (isWebSubscription &&
                user.subscriptionStatus !== SUBSCRIPTION_STATUS.ACTIVE)) && (
              <button
                className="
              mx-auto 
              mx-md-0
              subscribe-btn
            "
                onClick={() => navigateToSubscription()}
              >
                Subscribe
              </button>
            )}
          </div>
          {(user.subscriptionPlatform === SUBSCRIPTION_PLATFORM.ANDROID ||
            user.subscriptionPlatform === SUBSCRIPTION_PLATFORM.IOS) &&
            user.subscriptionStatus !== SUBSCRIPTION_STATUS.EXPIRED && (
              <div className="subscription-message">
                {`To manage or cancel your subscription, please go to the ${
                  user.subscriptionPlatform === SUBSCRIPTION_PLATFORM.ANDROID
                    ? "Google Play"
                    : "Apple App"
                } Store.`}
              </div>
            )}
          <Row>
            <Panel
              xs={12}
              lg={6}
              panelClass="mb-3 mb-lg-0"
              title="Current Plan"
              buttonText={
                isWebSubscription &&
                user.subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE
                  ? "Change Subscription"
                  : null
              }
              isSpinner={spinner(GET_SUBSCRIPTIONS) > -1}
              callback={() => navigateToSubscription()}
            >
              <DetailsRow
                label="Plan"
                value={`${getFormattedValue(
                  subscriptionPlan?.plan,
                  (plan) => SUBSCRIPTION_PLAN_MAPPING[plan]
                )} ${
                  subscriptionPlan?.isFreeTrialPeriod ? "(Free Trial)" : ""
                } ${showNoOfListings(subscriptionPlan)}`}
              />
              <DetailsRow
                label="Plan Type"
                value={getFormattedValue(
                  subscriptionPlan?.planType,
                  (planType) => SUBSCRIPTION_PLAN_TYPE_MAPPING[planType]
                )}
              />
              <DetailsRow
                label="Next Payment Date"
                value={getFormattedValue(
                  subscriptionPlan?.nextPaymentDate,
                  formatUnixTimestampToDate
                )}
              />
              <DetailsRow
                label="Next Payment Amount"
                value={getFormattedValue(
                  subscriptionPlan?.amount,
                  null,
                  `${subscriptionPlan?.currencyCode ?? "$"} ${
                    subscriptionPlan?.amount
                  }`
                )}
              />
            </Panel>
            <Panel
              xs={12}
              lg={6}
              title={"Billing Details"}
              buttonText={
                isWebSubscription || canAllowWebSubscription
                  ? "Edit Card"
                  : null
              }
              isSpinner={spinner(GET_SUBSCRIPTIONS) > -1}
              callback={() => changeCard(userRole)}
              icon={EditCardIcon}
            >
              <DetailsRow
                label={"Card Holder Name"}
                value={billingDetails?.cardHolderName || "-"}
                className="text-capitalize line-clamp"
              />
              <DetailsRow
                label={"Card Type"}
                value={billingDetails?.brand || "-"}
                className="text-capitalize"
              />
              <DetailsRow
                label={"Card Number"}
                value={
                  billingDetails?.lastFour
                    ? `xxxx xxxx xxxx ${billingDetails?.lastFour}`
                    : "-"
                }
              />
              <DetailsRow
                label={"Expiry"}
                value={billingDetails?.expiry || "-"}
              />
            </Panel>

            <Panel
              xs={12}
              panelClass="mt-4"
              title="Subscription Details"
              isSpinner={spinner(GET_SUBSCRIPTIONS) > -1}
            >
              <ReactTable
                showPagination={false}
                className="subscription-table"
                columns={columns}
                data={[...subscriptionDetails].sort((a, b) => b.id - a.id)}
              />
            </Panel>
          </Row>
        </div>
      </MainContentWrapper>
      <Footer />
    </>
  );
};

const mapStateToProps = (state) => ({
  user: state.authReducer.user,
  userRole: state.authReducer.userRole,
  token: state.subscriptionReducer.token,
  spinnerActions: state.systemReducer.spinnerActions,
  billingDetails: state.subscriptionReducer.billingDetails,
  subscriptionPlan: state.subscriptionReducer.subscriptionPlan,
  subscriptionDetails: state.subscriptionReducer.subscriptionDetails,
});

const mapDispatchToProps = (dispatch) => ({
  resetToken: () => dispatch(subscriptionActions.resetToken()),
  changeCard: (roleId) => dispatch(subscriptionActions.changeCard(roleId)),
  updateUser: (data) => dispatch(authActions.updateUser(data)),
  fetchInvoice: (subscriptionId, body) =>
    dispatch(subscriptionActions.fetchInvoice(subscriptionId, body)),
  getSubscriptions: (roleId) =>
    dispatch(subscriptionActions.getSubscriptions(roleId)),
  cancelSubscription: (roleId, func) =>
    dispatch(subscriptionActions.cancelSubscription(roleId, func)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MySubscriptions);
